import { inject, Injectable, Renderer2 } from '@angular/core';
import { IPayout, ManageableStorageLocation } from '@luggagehero/shared/interfaces';
import { SharedPricingService } from '@luggagehero/shared/services/pricing';
import { SharedScriptService } from '@luggagehero/shared/services/script';
import { SharedTranslateService } from '@luggagehero/shared/services/translation';
import moment from 'moment';
import { mergeMap } from 'rxjs/operators';

import { InvoiceServiceStrategy } from './invoice-service.strategy';
import { exportPdf } from './pdf-export.util';

/**
 * Alternate v2 invoice implementation used for exploring a redesigned
 * payout invoice layout. Currently wired through the facade for v2 payouts
 * while the design is being iterated on. Once the layout is locked in, the
 * contents of this file will replace the original v2 implementation.
 *
 * Strings introduced by the redesign are hardcoded English with `// TODO i18n`
 * comments — they'll be migrated to INVOICE_V2_* keys once the design settles.
 */

interface Company {
  name: string;
  address: string;
  vatNumber?: string;
}

const DK_COMPANY: Company = {
  name: `LuggageHero A/S`,
  address: 'Aarhusgade 118, 2150 Nordhavn, Denmark',
  vatNumber: 'DK37611328',
};
const US_COMPANY: Company = {
  name: `LuggageHero, LLC`,
  address: '228 Park Ave S # 300, New York, NY 10003, USA',
};

const LOGO_URL = 'assets/logo_light_bg.png';
const DATE_FORMAT = 'YYYYMMDD';

// A4 content width: 595pt page width − default 40pt margins either side = 515pt.
const CONTENT_WIDTH = 515;
const BRAND_ORANGE = '#ff4b00';
const MUTED = '#666';
const LIGHT_RULE = '#d0d0d0';

const WalkInBonusName = 'WalkInBonus';
const WalkInBonusDesription = 'Walk-in Bonus';

@Injectable({ providedIn: 'root' })
export class SharedInvoiceV2AlternateService implements InvoiceServiceStrategy {
  private priceService = inject(SharedPricingService);
  private script = inject(SharedScriptService);
  private translate = inject(SharedTranslateService);

  private logoDataURL!: string;
  private isInitialized = false;

  public get isReady(): boolean {
    return this.logoDataURL ? true : false;
  }

  public init(renderer: Renderer2, _doc: unknown) {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = <HTMLCanvasElement>renderer.createElement('CANVAS');
      canvas.height = img.height;
      canvas.width = img.width;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
      }
      this.logoDataURL = canvas.toDataURL('image/png');
    };
    img.src = LOGO_URL;

    this.script
      .load('pdfmake')
      .pipe(mergeMap((_res) => this.script.load('vfs_fonts')))
      .subscribe();
  }

  public open(payout: IPayout, shop: ManageableStorageLocation) {
    exportPdf(this.getDocDefinition(payout, shop), 'open');
  }

  public print(payout: IPayout, shop: ManageableStorageLocation) {
    exportPdf(this.getDocDefinition(payout, shop), 'print');
  }

  public download(payout: IPayout, shop: ManageableStorageLocation) {
    exportPdf(this.getDocDefinition(payout, shop), 'download');
  }

  public getDocDefinition(
    payout: IPayout,
    shop: ManageableStorageLocation,
  ): {
    info: Record<string, string>;
    content: unknown;
    pageSize: string;
  } {
    return {
      info: this.getDocInfo(payout),
      content: this.getDocContent(payout, shop),
      pageSize: 'A4',
    };
  }

  private getDocInfo(payout: IPayout): Record<string, string> {
    const date = moment(payout.created).format('YYYY-MM-DD');
    return {
      title: `[${date}] LuggageHero - invoice - ${payout.invoiceNumber}`,
      author: 'LuggageHero',
      creator: 'LuggageHero',
      producer: 'LuggageHero',
      subject: `LuggageHero invoice ${payout.invoiceNumber}`,
      keywords: `LuggageHero invoice ${payout.invoiceNumber}`,
    };
  }

  private getDocContent(payout: IPayout, shop: ManageableStorageLocation): unknown[] {
    return [
      this.getHeader(payout, shop),
      this.getSalesSummary(payout),
      this.getFeesBreakdown(payout, shop),
      this.getPayoutReconciliation(payout, shop),
      this.getFootnote(payout),
      this.getCompanyFooter(shop),
    ];
  }

  // -------------------------------------------------------------------------
  // Header: logo + hero net-payout card | title line + brand rule | billing
  // -------------------------------------------------------------------------

  private getHeader(payout: IPayout, shop: ManageableStorageLocation): unknown {
    const balance = payout.data.balance ?? 0;
    const isPayout = balance >= 0;
    const fundsDate = moment(payout.created).add(8, 'days').format('L');
    const issuedDate = moment(payout.created).format('L');

    return [
      // Top band: logo (left) and net-payout hero (right)
      {
        columns: [
          { image: this.logoDataURL, width: 100, alignment: 'left' },
          {
            width: '*',
            stack: [
              {
                text: isPayout ? 'NET PAYOUT TO YOU' : 'BALANCE OWED', // TODO i18n
                fontSize: 9,
                color: MUTED,
                characterSpacing: 1.5,
                alignment: 'right',
              },
              {
                text: this.formatPrice(Math.abs(balance), payout.currency),
                fontSize: 24,
                bold: true,
                color: isPayout ? '#1a1a1a' : BRAND_ORANGE,
                alignment: 'right',
                margin: [0, 4, 0, 0],
              },
              {
                text: isPayout
                  ? `available ${fundsDate}` // TODO i18n
                  : 'carried over to your next invoice', // TODO i18n
                fontSize: 9,
                color: MUTED,
                italics: true,
                alignment: 'right',
                margin: [0, 2, 0, 0],
              },
            ],
          },
        ],
      },

      // Title line: "INVOICE  #XXXXX" + issued date aligned right
      {
        columns: [
          {
            width: '*',
            text: [
              { text: this.translate.instant('INVOICE_V2_TITLE').toUpperCase(), bold: true },
              { text: `   #${payout.invoiceNumber}`, color: MUTED },
            ],
            fontSize: 18,
            characterSpacing: 1.5,
          },
          {
            width: 'auto',
            text: `issued ${issuedDate}`, // TODO i18n
            fontSize: 9,
            color: MUTED,
            alignment: 'right',
            margin: [0, 8, 0, 0],
          },
        ],
        margin: [0, 32, 0, 8],
      },

      // Brand accent rule
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1.5, lineColor: BRAND_ORANGE }],
        margin: [0, 0, 0, 20],
      },

      // Billing block (left) + Period (right)
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: shop.name, bold: true },
              ...(shop.vatNumber
                ? [
                    {
                      text: `${this.translate.instant('INVOICE_V2_VAT_NUMBER_LABEL')}: ${shop.vatNumber.toUpperCase()}`,
                      color: MUTED,
                      fontSize: 10,
                    },
                  ]
                : []),
              { text: this.getShopAddress(shop), color: MUTED, fontSize: 10 },
            ],
          },
          {
            width: 'auto',
            stack: [
              { text: 'PERIOD', fontSize: 9, color: MUTED, characterSpacing: 1.5, alignment: 'right' }, // TODO i18n
              {
                text: this.getHumanPeriod(payout),
                bold: true,
                alignment: 'right',
                margin: [0, 2, 0, 0],
              },
            ],
          },
        ],
      },
    ];
  }

  // -------------------------------------------------------------------------
  // Sales summary: single line stating gross sales (+ tips if any)
  // -------------------------------------------------------------------------

  private getSalesSummary(payout: IPayout): unknown {
    const totalRevenue = this.formatPrice(payout.data.totalRevenue ?? 0, payout.currency);
    const totalTipsConverted = payout.data.totalTipsConverted ?? 0;
    const tipsFragment =
      totalTipsConverted > 0
        ? ` (incl. ${this.formatPrice(totalTipsConverted, payout.currency)} in tips)` // TODO i18n
        : '';

    return {
      text: `Total sales of ${totalRevenue} on your behalf${tipsFragment}`, // TODO i18n
      margin: [0, 32, 0, 0],
    };
  }

  // -------------------------------------------------------------------------
  // Fees breakdown: section heading + fee rows + total incl. VAT
  // -------------------------------------------------------------------------

  private getFeesBreakdown(payout: IPayout, shop: ManageableStorageLocation): unknown {
    const body = [];
    const showVat = !shop.region.match(/us/i);
    const commission = payout.data.commission ?? 0;
    const totalServiceFees = payout.data.totalServiceFees ?? 0;
    const subTotal = payout.data.subTotal ?? 0;
    const vat = payout.data.vat ?? 0;
    const totalInclVat = subTotal + vat;

    body.push([
      this.translate.instant('INVOICE_V2_COMMISSION'),
      { text: this.formatPrice(commission, payout.currency), alignment: 'right' },
    ]);

    if (totalServiceFees > 0) {
      body.push([
        this.translate.instant('INVOICE_V2_SERVICE_FEES'),
        { text: this.formatPrice(totalServiceFees, payout.currency), alignment: 'right' },
      ]);
    }

    const minRevenueAdj = payout.data.minimumRevenueAdjustmentConverted;
    if (minRevenueAdj != null && minRevenueAdj < 0) {
      body.push([
        this.translate.instant('INVOICE_V2_MINIMUM_REVENUE_GUARANTEE'),
        { text: this.formatPrice(minRevenueAdj, payout.currency), alignment: 'right' },
      ]);
    }

    if (payout.data.transactions) {
      payout.data.transactions
        .filter((t) => t.description !== WalkInBonusDesription)
        .forEach((t) => {
          const sign = t.amount < 0 ? '' : '- ';
          body.push([
            t.description,
            { text: `${sign} ${this.formatPrice(t.amount, payout.currency)}`, alignment: 'right' },
          ]);
        });

      const walkInBonuses = payout.data.transactions.filter((t) => {
        const meta = t.metadata as { bonusName?: string } | undefined;
        return meta?.bonusName === WalkInBonusName || t.description === WalkInBonusDesription;
      });

      if (walkInBonuses.length > 0) {
        const totalBonusAmount = walkInBonuses.reduce((sum, t) => sum + t.amount, 0);
        const description =
          walkInBonuses.length > 1
            ? `${walkInBonuses.length}× ${walkInBonuses[0].description}`
            : walkInBonuses[0].description;
        body.push([
          description,
          { text: `- ${this.formatPrice(totalBonusAmount, payout.currency)}`, alignment: 'right' },
        ]);
      }
    }

    if (showVat) {
      body.push([
        { text: this.translate.instant('INVOICE_V2_TOTAL_EXCL_VAT'), margin: [0, 4, 0, 0] },
        {
          text: this.formatPrice(subTotal, payout.currency),
          alignment: 'right',
          margin: [0, 4, 0, 0],
        },
      ]);
      body.push([
        this.translate.instant('INVOICE_V2_VAT', undefined, {
          percent: this.formatPercentage(vat, subTotal),
        }),
        { text: this.formatPrice(vat, payout.currency), alignment: 'right' },
      ]);
    }

    const totalLabelKey = showVat ? 'INVOICE_V2_TOTAL_INCL_VAT' : 'INVOICE_V2_TOTAL';
    body.push([
      { text: this.translate.instant(totalLabelKey), bold: true, margin: [0, 4, 0, 0] },
      {
        text: this.formatPrice(totalInclVat, payout.currency),
        bold: true,
        alignment: 'right',
        margin: [0, 4, 0, 0],
      },
    ]);

    return [
      this.sectionHeading('FEES BREAKDOWN'), // TODO i18n
      this.thinRule(),
      {
        layout: 'noBorders',
        table: { widths: ['*', 'auto'], body },
        margin: [0, 8, 0, 0],
      },
    ];
  }

  // -------------------------------------------------------------------------
  // Payout reconciliation: shows how the net payout is derived
  // -------------------------------------------------------------------------

  private getPayoutReconciliation(payout: IPayout, shop: ManageableStorageLocation): unknown {
    const showVat = !shop.region.match(/us/i);
    const body = [];

    body.push([
      this.translate.instant('INVOICE_V2_TOTAL_SALES'),
      { text: this.formatPrice(payout.data.totalRevenue ?? 0, payout.currency), alignment: 'right' },
    ]);

    if ((payout.data.inShopRevenue ?? 0) > 0 || (payout.data.paidDirectly ?? 0) > 0) {
      let inShopRevenue = payout.data.inShopRevenue ?? 0;
      if (payout.data.paidDirectly && payout.data.paidDirectly > 0) {
        inShopRevenue = payout.data.paidDirectly;
        if ((payout.data.inShopTips ?? 0) > 0) {
          inShopRevenue = inShopRevenue - (payout.data.inShopTips ?? 0);
        }
      }
      body.push([
        this.translate.instant('INVOICE_V2_RECEIVED_DIRECTLY'),
        { text: `- ${this.formatPrice(inShopRevenue, payout.currency)}`, alignment: 'right' },
      ]);
    }

    const totalCostOfService = (payout.data.subTotal ?? 0) + (payout.data.vat ?? 0);
    const feesKey = showVat ? 'INVOICE_V2_LUGGAGEHERO_FEES_INCL_VAT' : 'INVOICE_V2_LUGGAGEHERO_FEES';
    body.push([
      this.translate.instant(feesKey),
      { text: `- ${this.formatPrice(totalCostOfService, payout.currency)}`, alignment: 'right' },
    ]);

    if ((payout.data.totalTipsConverted ?? 0) > 0) {
      body.push([
        'Tips forwarded to you', // TODO i18n
        {
          text: this.formatPrice(payout.data.totalTipsConverted ?? 0, payout.currency),
          alignment: 'right',
        },
      ]);
    }

    if (payout.data.previousBalance) {
      body.push([
        this.translate.instant('INVOICE_V2_PREVIOUS_BALANCE'),
        { text: this.formatPrice(payout.data.previousBalance, payout.currency), alignment: 'right' },
      ]);
    }

    const balance = payout.data.balance ?? 0;
    body.push([
      { text: 'Net payout', bold: true, margin: [0, 4, 0, 0] }, // TODO i18n
      {
        text: this.formatPrice(balance, payout.currency),
        bold: true,
        alignment: 'right',
        margin: [0, 4, 0, 0],
      },
    ]);

    return [
      this.sectionHeading('PAYOUT RECONCILIATION'), // TODO i18n
      this.thinRule(),
      {
        layout: 'noBorders',
        table: { widths: ['*', 'auto'], body },
        margin: [0, 8, 0, 0],
      },
    ];
  }

  // -------------------------------------------------------------------------
  // Footnote (funds available / undeducted tax)
  // -------------------------------------------------------------------------

  private getFootnote(payout: IPayout): unknown {
    const balance = payout.data.balance ?? 0;
    const notes: unknown[] = [];

    if (balance > 0) {
      const fundsAvailable = moment(payout.created).add(8, 'days').format('L');
      notes.push(this.translate.instant('INVOICE_V2_FUNDS_AVAILABLE_ON', undefined, { date: fundsAvailable }));
    } else if (balance < 0) {
      notes.push(this.translate.instant('INVOICE_V2_BALANCE_CARRIED_OVER'));
    }

    if (payout.data.taxes != null && payout.data.taxes > 0) {
      notes.push(
        `${this.translate.instant('INVOICE_V2_UNDEDUCTED_TAX')}: ${this.formatPrice(
          payout.data.taxes,
          payout.currency,
        )}`,
      );
    }

    if (notes.length === 0) return '';

    return {
      stack: notes.map((text) => ({ text, fontSize: 10, color: MUTED, italics: true })),
      margin: [0, 24, 0, 0],
    };
  }

  // -------------------------------------------------------------------------
  // Legal company footer
  // -------------------------------------------------------------------------

  private getCompanyFooter(shop: ManageableStorageLocation): unknown {
    // TODO: this should be based on the LuggageHero Stripe account used for the payout (DK/US)
    const company = shop.payoutOrigin === 'us' ? US_COMPANY : DK_COMPANY;
    let text = `${company.name} • ${company.address}`;
    if (company.vatNumber) {
      text += ` • ${this.translate.instant('INVOICE_V2_VAT_NUMBER_LABEL')}: ${company.vatNumber}`;
    }
    return {
      text,
      alignment: 'center',
      fontSize: 8,
      color: '#888',
      margin: [0, 48, 0, 0],
    };
  }

  // -------------------------------------------------------------------------
  // Shared style primitives
  // -------------------------------------------------------------------------

  private sectionHeading(label: string): unknown {
    return {
      text: label,
      fontSize: 10,
      bold: true,
      color: MUTED,
      characterSpacing: 2,
      margin: [0, 32, 0, 4],
    };
  }

  private thinRule(): unknown {
    return {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 0.5, lineColor: LIGHT_RULE }],
    };
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  private getShopAddress(shop: ManageableStorageLocation): string {
    return shop.address.formattedAddress.replace('Storbritannien', 'United Kingdom');
  }

  /**
   * Formats the payout period in a human-readable way:
   *   full calendar month   → "March 2026"
   *   within one month      → "March 1 – 31, 2026"
   *   within one year       → "Mar 1 – May 15, 2026"
   *   spans years           → "Dec 15, 2025 – Mar 14, 2026"
   *   fallback              → payout.key verbatim
   */
  private getHumanPeriod(payout: IPayout): string {
    const keyParts = payout.key.split('-');
    if (keyParts.length !== 2) return payout.key;
    const from = moment(keyParts[0], DATE_FORMAT);
    const to = moment(keyParts[1], DATE_FORMAT);
    if (!from.isValid() || !to.isValid()) return payout.key;

    const isFullMonth =
      from.isSame(from.clone().startOf('month'), 'day') && to.isSame(from.clone().endOf('month'), 'day');
    if (isFullMonth) return from.format('MMMM YYYY');

    if (from.isSame(to, 'year') && from.isSame(to, 'month')) {
      return `${from.format('MMMM D')} – ${to.format('D, YYYY')}`;
    }
    if (from.isSame(to, 'year')) {
      return `${from.format('MMM D')} – ${to.format('MMM D, YYYY')}`;
    }
    return `${from.format('MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
  }

  private formatPercentage(dividend: number | undefined, divisor: number): string {
    if (!divisor) return '0%';
    const percentage = ((dividend ?? 0) / divisor) * 100;
    if (!isFinite(percentage)) return '0%';
    return `${Math.round(percentage).toFixed(0)}%`;
  }

  private formatPrice(price: number, currency: string, removeZeroDecimals = false) {
    return this.priceService.format(price, currency, 2, removeZeroDecimals);
  }
}
